// @ts-ignore
import React, { forwardRef, startTransition, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import BackupTableOutlinedIcon from '@mui/icons-material/BackupTableOutlined';

import { featureFlagsMap, hasRole, useHasActiveFeatureFlag, useMountedEffect } from '@clatter/platform';
import { routesNames } from '../../../routes';
import { pagesMap } from '../../../constants';
import { userRolesMap } from '../../../helpers/auth';
import { FolderManagerItemTypes } from '../../../interfaces';
import { useGetUserRootFoldersQuery } from '../../../services';
import { assetManagerUISelectors, authSelectors, setSidebarStateItems } from '../../../store';

import { IconButton } from '../IconButton/IconButton';
import layoutBoardIcon from '../../../../assets/icons/layout-board.svg';
import homeIcon from '../../../../assets/icons/v2/home.svg';
import entitiesIcon from '../../../../assets/icons/v2/entities.svg';
import workspaceTemplatesIcon from '../../../../assets/icons/v2/workspace_templates.svg';
import sharedWithMeIcon from '../../../../assets/icons/v2/shared_with_me.svg';
import myAssetsIcon from '../../../../assets/icons/v2/my_assets_outlined.svg';
import manageGroupsIcon from '../../../../assets/icons/v2/manage_groups.svg';
import companyAssetsIcon from '../../../../assets/icons/v2/company_assets.svg';
import trashIcon from '../../../../assets/icons/v2/trash.svg';
import closeIcon from '../../../../assets/icons/x.svg';
import menuIcon from '../../../../assets/icons/menu-2.svg';
import { NgNavItem } from '../NgNavItem/NgNavItem';

interface SideNavbarProps {
  topNavbarHeight: number | undefined;
  expanded?: boolean;
  ref: any;
}
interface ContainerProps {
  ref: any;
  $topNavbarHeight: number | undefined;
  $expanded: boolean;
}

const SideNavbarContainer = styled.div<ContainerProps>`
  position: sticky;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  left: 0;
  top: ${({ $topNavbarHeight }) => $topNavbarHeight}px;
  height: calc(100vh - ${({ $topNavbarHeight }) => $topNavbarHeight}px);
  min-width: ${({ $expanded }) => ($expanded ? '240px' : '64px')};

  border-right: 1px solid #ccc;
  background-color: #fff;
  padding: 8px;
  transition: width 0.3s ease;
`;

const NavList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SubNavList = styled.ul<{ $expandedParent: boolean; $sidebarExpanded: boolean }>`
  margin: 0 0 0 24px;
  padding: 0;
  list-style: none;
  display: ${({ $expandedParent }) => ($expandedParent ? 'block' : 'none')};

  .sub-label {
    margin-left: 10px;
    color: #555;
    opacity: ${({ $sidebarExpanded }) => ($sidebarExpanded ? 1 : 0)};
    visibility: ${({ $sidebarExpanded }) => ($sidebarExpanded ? 'visible' : 'hidden')};
    transition: opacity 0.2s ease, visibility 0.2s ease;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ICON_SIZE = '1.5rem';
const MENU_ITEMS_ORDER = [
  'my-workspace',
  pagesMap.workspaceTemplates.slug,
  'company-assets',
  'my-assets',
  'themes',
  'entities',
  'shared',
  'groups',
  'reports',
  'trash',
];

const sortBySlugOrder = (array: any[], slugOrder: string[]) => {
  return array.sort((a, b) => {
    const indexA = slugOrder.indexOf(a.slug);
    const indexB = slugOrder.indexOf(b.slug);
    return indexA - indexB;
  });
};

const IconImg = styled.img`
  width: 1.5rem;
  height: 1.5rem;
  display: block;
`;

const iconNameToComponentMap: Record<string, React.ReactNode> = {
  LayoutBoardIcon: <IconImg src={layoutBoardIcon} alt="layout board" />,
  FolderIcon: <FolderOutlinedIcon />,
  HomeIcon: <IconImg src={homeIcon} alt="home" />,
  EntitiesIcon: <IconImg src={entitiesIcon} alt="entities" />,
  WorkspaceTemplatesIcon: <IconImg src={workspaceTemplatesIcon} alt="workspace templates" />,
  SharedWithMeIcon: <IconImg src={sharedWithMeIcon} alt="shared with me" />,
  MyAssetsIcon: <IconImg src={myAssetsIcon} alt="my assets" />,
  CompanyAssetsIcon: <IconImg src={companyAssetsIcon} alt="company assets" />,
  ManageGroupsIcon: <IconImg src={manageGroupsIcon} alt="manage groups" />,
  TrashIcon: <IconImg src={trashIcon} alt="trash" />,
  CloseIcon: <IconImg src={closeIcon} alt="close" />,
  MenuIcon: <IconImg src={menuIcon} alt="menu" />,
  ThemesIcon: <BackupTableOutlinedIcon />,
  AssessmentIcon: <AssessmentIcon />,
  ShowChartIcon: <ShowChartIcon />,
};

const getIconFromString = (iconName: string) => iconNameToComponentMap?.[iconName] || null;

const getMenuItemIconNameBySlug = (slug: string) => {
  switch (slug) {
    case 'trash':
      return 'TrashIcon';
    case 'reports':
      return 'AssessmentIcon';
    case 'my-workspace':
      return 'HomeIcon';
    case pagesMap.workspaceTemplates.slug:
      return 'WorkspaceTemplatesIcon';
    case 'entities':
      return 'EntitiesIcon';
    case 'shared':
      return 'SharedWithMeIcon';
    case 'groups':
      return 'ManageGroupsIcon';
    case 'my-assets':
      return 'MyAssetsIcon';
    case 'company-assets':
      return 'CompanyAssetsIcon';
    case 'themes':
      return 'ThemesIcon';
    default:
      return 'FolderIcon';
  }
};

const normalizeSlug = (slug: string) => (slug.startsWith('/') ? slug : `/${slug}`);

export const SideNavbar: React.FC<SideNavbarProps> = forwardRef(({ topNavbarHeight }, ref) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { slug: slugFromParams } = useParams();

  const activeUser = useSelector(authSelectors.getUserAuthState)?.user;
  const [expanded, setExpanded] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const toggleSection = (slug: string) => setOpenSections((prev) => ({ ...prev, [slug]: !prev[slug] }));

  const { data: sidebarFoldersData, isLoading } = useGetUserRootFoldersQuery();
  const sidebarState = useSelector(assetManagerUISelectors.getSidebarState);

  const displayEntitiesTab = useHasActiveFeatureFlag(featureFlagsMap.MSM.micrositesEntities);
  const enableReports = useHasActiveFeatureFlag(featureFlagsMap.NG.reports);
  const enableReportsDocumentsGenerated = useHasActiveFeatureFlag(featureFlagsMap.NG.reportsDocumentsGenerated);
  const enableReportsFilesDownloaded = useHasActiveFeatureFlag(featureFlagsMap.NG.reportsFilesDownloaded);

  const handleNavItemClick = (data: any) => {
    if (data?.children?.length) {
      if (!expanded) {
        setExpanded(true);

        // prevent collapsing section,
        // if collapsed toggle to open
        if (openSections?.[data.slug]) {
          return;
        }
      }

      toggleSection(data.slug);
      return;
    }
    if (data?.type === FolderManagerItemTypes.FOLDER) {
      startTransition(() => navigate(routesNames.folders(data.slug, data.id)));
    }
    if (data?.slug) {
      startTransition(() => navigate(data.slug.startsWith('/') ? data.slug : `/${data.slug}`));
    }
  };

  useMountedEffect(() => {
    if (isLoading) return;

    const foldersItems = (sidebarFoldersData || [])
      .map((item: any) => {
        if (item?.displayName?.toLowerCase() === 'entities' || item?.name?.toLowerCase() === 'entities') {
          return displayEntitiesTab
            ? {
                ...item,
                displayName: item?.displayName || item?.name,
                icon: getMenuItemIconNameBySlug(item?.slug),
              }
            : null;
        }

        return {
          ...item,
          displayName: item?.displayName || item?.name,
          icon: getMenuItemIconNameBySlug(item?.slug),
        };
      })
      .filter(Boolean);

    const customNavItems: any[] = [
      hasRole(userRolesMap.ngAdmin, activeUser)
        ? {
            slug: 'groups',
            displayName: 'Manage Groups',
            icon: getMenuItemIconNameBySlug('groups'),
          }
        : null,
      {
        slug: 'shared',
        displayName: 'Shared With Me',
        icon: getMenuItemIconNameBySlug('shared'),
        type: FolderManagerItemTypes.FOLDER,
      },
    ].filter(Boolean);

    if (hasRole(pagesMap.workspaceTemplates.accessLevel, activeUser)) {
      customNavItems.push({
        slug: pagesMap.workspaceTemplates.slug,
        displayName: pagesMap.workspaceTemplates.displayName,
        icon: getMenuItemIconNameBySlug(pagesMap.workspaceTemplates.slug),
      });
    }

    if (enableReports && hasRole(userRolesMap.ngAdmin, activeUser)) {
      customNavItems.push({
        slug: 'reports',
        displayName: 'Reports',
        icon: 'AssessmentIcon',
        children: [
          enableReportsDocumentsGenerated && {
            slug: `${routesNames.reports}/${routesNames.reportsDocumentsGenerated}`,
            displayName: 'Documents Generated',
            icon: 'ShowChartIcon',
          },
          enableReportsFilesDownloaded && {
            slug: `${routesNames.reports}/${routesNames.reportsFilesDownloaded}`,
            displayName: 'Files Downloaded',
            icon: 'ShowChartIcon',
          },
        ].filter(Boolean),
      });
    }

    const navItems = [{ slug: 'home', displayName: 'Home', icon: 'HomeIcon' }, ...foldersItems, ...customNavItems];
    const sortedItems = sortBySlugOrder(navItems, MENU_ITEMS_ORDER);

    if (sortedItems?.length) {
      dispatch(setSidebarStateItems(sortedItems));

      const defaultOpen: Record<string, boolean> = {};
      sortedItems.forEach((i) => {
        if (i.children?.length) defaultOpen[i.slug] = true;
      });
      setOpenSections(defaultOpen);
    }
  }, [
    isLoading,
    slugFromParams,
    displayEntitiesTab,
    sidebarFoldersData,
    enableReports,
    enableReportsFilesDownloaded,
    enableReportsDocumentsGenerated,
    activeUser,
  ]);

  return (
    <SideNavbarContainer ref={ref} $expanded={expanded} $topNavbarHeight={topNavbarHeight}>
      <NavList>
        {(sidebarState?.items || []).map((item: any) => {
          const isParent = Array.isArray(item.children) && item.children.length > 0;
          const isOpen = !!openSections[item.slug];
          const parentPath = normalizeSlug(item.slug);
          const isParentActive =
            location.pathname.startsWith(parentPath) ||
            (isParent && item.children.some((c: any) => location.pathname.startsWith(normalizeSlug(c.slug))));

          return (
            <React.Fragment key={item?.slug}>
              <NgNavItem
                item={item}
                expanded={expanded}
                onClick={handleNavItemClick}
                isOpen={isOpen}
                isParent={isParent}
                active={isParentActive}
              />

              {isParent && (
                <SubNavList $expandedParent={expanded && isOpen} $sidebarExpanded={expanded}>
                  {item.children.map((child: any) => {
                    const childPath = normalizeSlug(child.slug);
                    const isChildActive = location.pathname.startsWith(childPath);

                    return (
                      <NgNavItem
                        key={child.slug}
                        item={child}
                        expanded={expanded}
                        active={isChildActive}
                        variant="sub"
                        onClick={handleNavItemClick}
                      />
                    );
                  })}
                </SubNavList>
              )}
            </React.Fragment>
          );
        })}
      </NavList>

      <IconButton onClick={() => setExpanded(!expanded)} className="icon-button" size={ICON_SIZE}>
        {expanded ? getIconFromString('CloseIcon') : getIconFromString('MenuIcon')}
      </IconButton>
    </SideNavbarContainer>
  );
});
